import { useEffect, type ReactNode } from 'react'
import { useAuthStore } from './authStore'
import { getSupabaseClient, isSupabaseConfigured } from '@/repository/supabase/client'
import { bindReposToSession } from '@/repository/supabase/bindReposToSession'
import { setWorkspaceIdResolver, DEFAULT_WORKSPACE_ID } from '@/types/workspace'
import { withTimeout } from '@/lib/withTimeout'
import { queryActiveWorkspaceId } from './workspaceSelection'
import { consumeIntentionalSignOut, markSessionExpired } from './sessionFlags'
import { clearUserDataLocalStorage } from '@/components/layout/dataBackupKeys'

/**
 * Hard cap on the initial `getSession()` call. Supabase JS reads from
 * localStorage synchronously in the happy path, but when the stored
 * access token is expired it kicks off a token-refresh network round-
 * trip. That refresh can hang silently (mobile network blip, Cloudflare
 * hiccup, Supabase auth unreachable) — neither resolving nor rejecting.
 * Without this cap, `setLoading(false)` never fires from either the
 * `.then` or `.catch` and the user sits on the bg-slate-950 placeholder
 * forever. v0.8.18 fix: race the call and treat the timeout as "no
 * session," letting the router fall through to the landing.
 */
const GET_SESSION_TIMEOUT_MS = 6000

// Register the workspace resolver exactly once at module load. This is
// what flips `getCurrentWorkspaceId()` from "always local default" to
// "read the signed-in user's workspace id from auth state."
setWorkspaceIdResolver(() => useAuthStore.getState().currentWorkspaceId ?? DEFAULT_WORKSPACE_ID)

/**
 * Top-level provider that owns the bridge between Supabase Auth and the
 * rest of the app. Responsibilities:
 *
 *   1. On mount, hydrate the store from the persisted session (if any).
 *   2. Subscribe to `onAuthStateChange` and keep the store in sync.
 *   3. After each session change, rebind the four repo singletons so
 *      stores/hooks transparently flip between local and cloud impls.
 *   4. Resolve and cache the user's workspace_id once signed in.
 *
 * The provider renders its children immediately — it never blocks UI on
 * auth resolution. Components that care about "is the user signed in?"
 * read `useAuth().isLoading` and degrade gracefully. The local-first
 * codepath keeps working throughout the hydration window.
 *
 * Mount this once at the root of the tree, above any consumer of
 * `useAuth` or any data-fetching hook (`useDebts`, `useLedger`, ...).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const setSession = useAuthStore((s) => s.setSession)
  const setLoading = useAuthStore((s) => s.setLoading)
  const setCurrentWorkspaceId = useAuthStore((s) => s.setCurrentWorkspaceId)
  // v0.8.46 — workspace resolve error surfacing
  const setWorkspaceResolveError = useAuthStore((s) => s.setWorkspaceResolveError)
  const setWorkspaceResolveAttempted = useAuthStore((s) => s.setWorkspaceResolveAttempted)

  useEffect(() => {
    // Local-only build: no client, nothing to subscribe to. Flip loading
    // off so the UI doesn't sit on a spinner forever and bail out.
    if (!isSupabaseConfigured()) {
      setLoading(false)
      // Still bind repos defensively — `bindReposToSession(null)` is the
      // no-op path that confirms local repos are in place.
      void bindReposToSession(null)
      return
    }

    const client = getSupabaseClient()
    if (!client) {
      setLoading(false)
      return
    }

    let cancelled = false

    // Initial hydration. Supabase JS reads from localStorage synchronously
    // for the first call, so this resolves quickly when there's a stored
    // session.
    //
    // **`isLoading` is gated ONLY on session hydration.** The cloud
    // round-trip (`resolveAndCacheWorkspaceId` + `bindReposToSession`)
    // runs in the background after `setLoading(false)` has fired. This
    // is load-bearing: if the workspace query hangs (expired token doing
    // a refresh that never returns, Cloudflare blip, RLS quirk that
    // returns neither data nor error), the UI used to be stuck on the
    // bg-slate-950 placeholder forever because the `await` inside the
    // try-block never completed → finally never ran → setLoading(false)
    // never fired. v0.8.11 moved the flag flip BEFORE the cloud work so
    // the UI renders the moment we know the user's session state, even
    // if cloud-side resolution is slow / broken. `bindReposToSession`
    // already gracefully falls back to local repos when workspaceId is
    // null, so a hung workspace query just leaves the user in local
    // mode (the same as anonymous) — still functional, no blue screen.
    //
    // **Ordering for the cloud chain is still load-bearing** (separate
    // v0.8.9 fix): when a session exists, resolve workspace id first,
    // then bind. Otherwise bindReposToSession sees workspaceId === null
    // and stays on local repos.
    // Race getSession against a 6s timeout — see GET_SESSION_TIMEOUT_MS
    // comment for the silent-hang failure mode this guards. On timeout
    // we get the `null`-session fallback and the router treats it as
    // anonymous, surfacing the landing rather than the blue placeholder.
    const sessionTimeoutFallback = { data: { session: null }, _timedOut: true } as const
    void withTimeout(
      client.auth.getSession().then(
        (r) => ({ ...r, _timedOut: false }) as const,
        (err): typeof sessionTimeoutFallback => {
          console.error('[debtos] getSession rejected:', err)
          return sessionTimeoutFallback
        }
      ),
      GET_SESSION_TIMEOUT_MS,
      sessionTimeoutFallback
    ).then((result) => {
      if (cancelled) return
      if (result._timedOut) {
        console.warn(
          '[debtos] getSession timed out after',
          GET_SESSION_TIMEOUT_MS,
          'ms — falling through to anonymous mode'
        )
      }
      setSession(result.data.session)
      // Flip the loading flag immediately — UI can render now, with
      // either local (anonymous / pre-bind) or cloud (post-bind) data.
      setLoading(false)
      // Cloud-side post-init runs in the background.
      void runPostAuthInit(
        result.data.session,
        setCurrentWorkspaceId,
        setWorkspaceResolveError,
        setWorkspaceResolveAttempted,
        () => cancelled
      )
    })

    // Reactive subscription — fires on SIGNED_IN, SIGNED_OUT,
    // TOKEN_REFRESHED, USER_UPDATED. We treat any of them as "rebind to
    // current session state" since the singleton-swap is idempotent.
    // Wrapped in try/catch so a runtime error in any single transition
    // doesn't kill the subscription for subsequent events.
    //
    // Same ordering rule as above: resolve the workspace id FIRST when
    // a session arrives, then bind. SIGNED_OUT and pre-resolved
    // TOKEN_REFRESHED events skip the resolve step.
    const { data: sub } = client.auth.onAuthStateChange((event, session) => {
      if (cancelled) return
      // Unexpected SIGNED_OUT detection. A SIGNED_OUT event fires both
      // when the user clicks "Sign out" in the avatar menu AND when
      // Supabase's internal token-refresh attempt fails (refresh token
      // expired / revoked / unreachable auth endpoint). Distinguishing
      // them: useAuth.signOut sets the INTENTIONAL_SIGN_OUT_KEY flag
      // right before the call. If we see SIGNED_OUT and the flag is
      // absent, it's a refresh failure — raise the landing-page banner
      // so the user understands why they got bounced. Only raise if the
      // user was previously authenticated (avoid the spurious case
      // where SIGNED_OUT fires on an anonymous startup).
      if (event === 'SIGNED_OUT') {
        const wasAuthenticated = useAuthStore.getState().session !== null
        const intentional = consumeIntentionalSignOut()
        if (wasAuthenticated && !intentional) {
          markSessionExpired()
        }
        // v0.8.27 — privacy: wipe user-data localStorage keys so the
        // post-sign-out demo session can't render the previous account's
        // real data. Gated on `wasAuthenticated` to avoid wiping legitimate
        // anonymous-demo state on the spurious SIGNED_OUT that fires at
        // anonymous boot. Covers both intentional sign-out (avatar menu)
        // and unexpected token-refresh failure — both should clear the
        // local cache. Repo binding flips to local in the post-init below;
        // the wipe ensures local then reads empty, not stale-real.
        if (wasAuthenticated) {
          clearUserDataLocalStorage()
        }
      }
      setSession(session)
      // Same fire-and-forget rule as the initial hydration. The
      // post-auth chain (workspace resolution + cloud-repo binding)
      // doesn't block any UI state — if it hangs, the app still works
      // in local-mode rather than locking on a placeholder.
      void runPostAuthInit(
        session,
        setCurrentWorkspaceId,
        setWorkspaceResolveError,
        setWorkspaceResolveAttempted,
        () => cancelled,
        event
      )
    })

    // Cross-tab / cross-context catch-up on visibility change.
    //
    // The magic-link flow opens a NEW tab when the user clicks the link in
    // their email. Supabase JS v2 has a BroadcastChannel that's supposed to
    // propagate SIGNED_IN to the original tab, but two things break it on
    // mobile:
    //
    //   1. Chrome on Android suspends backgrounded tabs aggressively. While
    //      the user is in their email app, the original DebtOS tab is
    //      frozen. BroadcastChannel messages can be queued or dropped
    //      during suspension; the listener never fires when the tab is
    //      restored.
    //
    //   2. Some email clients (Gmail's in-app webview) open links in an
    //      isolated browsing context with a separate localStorage. The
    //      original tab can never learn about that sign-in via any
    //      cross-tab mechanism — the session token never reaches its
    //      storage.
    //
    // Case 1 is fixable: re-check the session when the tab becomes
    // visible. If the user signed in via the magic link in a sibling tab,
    // the session is now in localStorage (Supabase JS persists it). A
    // fresh `getSession()` reads it and our `onAuthStateChange` listener
    // fires SIGNED_IN, which transitions the UI.
    //
    // Case 2 (isolated webview) isn't fixable from JS — the original tab
    // simply never gets the credentials. SignInDialog handles that by
    // telling users to "open in browser." A user who comes back without
    // signing in just sees the same "Check your email" state, which is
    // accurate.
    const handleVisibilityChange = (): void => {
      if (cancelled) return
      if (document.visibilityState !== 'visible') return
      // Already authenticated — no need to round-trip.
      if (useAuthStore.getState().session) return
      void client.auth.getSession().then(({ data }) => {
        // If a session showed up while we were backgrounded, push it
        // through the same setter chain `onAuthStateChange` would use. We
        // *don't* manually rebind repos here — setting the session below
        // is enough because the SIGNED_IN event from the new
        // BroadcastChannel sync will fire on its own. But if for any
        // reason it didn't (suspended-tab edge case), this is the catch.
        if (data.session && !useAuthStore.getState().session) {
          setSession(data.session)
          // Kick off the same post-sign-in chain the listener would.
          void resolveAndCacheWorkspaceId(
            data.session.user.id,
            setCurrentWorkspaceId,
            setWorkspaceResolveError,
            setWorkspaceResolveAttempted
          ).then(() => bindReposToSession(data.session))
        }
      })
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      cancelled = true
      sub.subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [
    setSession,
    setLoading,
    setCurrentWorkspaceId,
    setWorkspaceResolveError,
    setWorkspaceResolveAttempted,
  ])

  return <>{children}</>
}

/**
 * Background post-auth init: resolves the workspace id, then binds the
 * cloud repos. Runs fire-and-forget from the AuthProvider effect — its
 * outcome doesn't gate `isLoading`. If anything in this chain throws or
 * hangs (workspace query never resolves, RLS quirk, network blip), the
 * app remains in local-repo mode and the user sees their existing
 * localStorage data instead of a blank loading placeholder.
 *
 * The `event` param is the `onAuthStateChange` event name when this is
 * called reactively. SIGNED_OUT clears the workspace cache; everything
 * else with a session re-resolves it (idempotent when already correct).
 */
async function runPostAuthInit(
  session: import('@supabase/supabase-js').Session | null,
  setCurrentWorkspaceId: (id: string | null) => void,
  setWorkspaceResolveError: (err: string | null) => void,
  setWorkspaceResolveAttempted: (attempted: boolean) => void,
  isCancelled: () => boolean,
  event?: string
): Promise<void> {
  try {
    if (event === 'SIGNED_OUT') {
      setCurrentWorkspaceId(null)
      // Reset the resolve-error state on sign-out so a re-sign-in
      // starts fresh (no leftover banner from the previous session).
      setWorkspaceResolveError(null)
      setWorkspaceResolveAttempted(false)
    } else if (session) {
      await resolveAndCacheWorkspaceId(
        session.user.id,
        setCurrentWorkspaceId,
        setWorkspaceResolveError,
        setWorkspaceResolveAttempted
      )
    }
    if (isCancelled()) return
    await bindReposToSession(session)
  } catch (err) {
    console.error(
      `[debtos] Post-auth init failed${event ? ` (${event})` : ''}:`,
      err instanceof Error ? err.message : err
    )
  }
}

/** Per-attempt hard cap on the workspace query. Five seconds is generous
 *  for a single-row indexed lookup; anything slower is broken-tier-Supabase
 *  territory. With the v0.8.46 retry chain (3 attempts at 5s each, plus
 *  backoff) the WORST-case wall time is ~19s — the user has already seen
 *  the app render anonymous (v0.8.11 moved isLoading off this path), so
 *  even the worst case is a background retry, not a stuck UI. */
const WORKSPACE_QUERY_TIMEOUT_MS = 5000

/**
 * v0.8.46 — Retry schedule. Exponential-ish backoff before attempts 2
 * and 3 (no delay before attempt 1). Total max time before giving up:
 * 0 + 5s + 0.3s + 5s + 0.9s + 5s ≈ 16.2s. Most transient failures
 * (PostgREST schema cache miss, JWT mid-refresh, Cloudflare edge
 * cold-start) clear within the first backoff.
 */
const WORKSPACE_RETRY_BACKOFFS_MS = [300, 900] as const // before attempts 2 and 3

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Fetches the signed-in user's workspace id and caches it in the store.
 * Solo-per-workspace v0.8.0: every user has exactly one row in
 * `workspaces`, created by the `handle_new_user` trigger.
 *
 * v0.8.46 — Retries up to 3 times with exponential-ish backoff to cover
 * transient Supabase failures (PostgREST schema cache miss, JWT
 * mid-refresh on cold start, network blip). Before this fix the
 * function was a one-shot — a single 5s timeout failure left the user
 * silently on local repos with no signal. The founder hit this on
 * 2026-06-23: cloud data intact in Supabase, empty Dashboard in app.
 *
 * Each attempt has its own 5s timeout. After all 3 attempts fail, we
 * surface the error via `setWorkspaceResolveError` so the
 * `<CloudSyncError>` banner can render with a Retry button. The
 * existing `setWorkspaceResolveAttempted(true)` flip happens regardless
 * of outcome so the UI can distinguish "haven't tried yet" from
 * "tried and settled."
 *
 * v0.8.50 — Selection goes through `queryActiveWorkspaceId` (membership-
 * based, deterministic) instead of an unordered `workspaces` limit(1), so
 * a household member with two workspaces always lands in the household.
 */
async function resolveAndCacheWorkspaceId(
  userId: string,
  setCurrentWorkspaceId: (id: string | null) => void,
  setWorkspaceResolveError: (err: string | null) => void,
  setWorkspaceResolveAttempted: (attempted: boolean) => void
): Promise<void> {
  const client = getSupabaseClient()
  if (!client) {
    setWorkspaceResolveAttempted(true)
    return
  }

  const maxAttempts = 1 + WORKSPACE_RETRY_BACKOFFS_MS.length

  let lastErr: string | null = null
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (attempt > 1) {
      const backoff = WORKSPACE_RETRY_BACKOFFS_MS[attempt - 2] ?? 0
      await wait(backoff)
    }

    const result = await withTimeout(
      queryActiveWorkspaceId(client, userId),
      WORKSPACE_QUERY_TIMEOUT_MS,
      { id: null, error: 'workspace query timed out' }
    )

    if (!result.error && result.id) {
      // Success — clear any prior error from a failed retry chain and stamp.
      setCurrentWorkspaceId(result.id)
      setWorkspaceResolveError(null)
      setWorkspaceResolveAttempted(true)
      return
    }

    lastErr = result.error ?? 'workspace row not found'
    console.warn(`[debtos] Workspace resolve attempt ${attempt}/${maxAttempts} failed:`, lastErr)
  }

  // All attempts exhausted. Stay on local repos (bindReposToSession will
  // see workspaceId=null and not bind) but surface the failure clearly.
  console.error('[debtos] Workspace resolve gave up after', maxAttempts, 'attempts')
  setCurrentWorkspaceId(null)
  setWorkspaceResolveError(lastErr ?? 'Unknown error')
  setWorkspaceResolveAttempted(true)
}
